import { api } from '../../api/client'
import { fieldOf, reportQueryParams, TABLE_LIMIT, type ReportQueryRequest } from '../cashflow/useCashflow'

/** The server caps a PDF at this many period columns (`GET /reports/query/pdf`, M8 D). */
export const MAX_PDF_PERIODS = 13

export class PdfExportError extends Error {
  readonly kind: 'too_many_periods' | 'failed'
  constructor(kind: 'too_many_periods' | 'failed') {
    super(`Report PDF export failed: ${kind}`)
    this.name = 'PdfExportError'
    this.kind = kind
  }
}

/** The name the server sent, when it is a plain `.pdf` file name; otherwise `report-{rows}-{start}-{end}.pdf` (the server's own pattern). */
export function pdfFilename(disposition: string | null, req: ReportQueryRequest): string {
  const fallback = `report-${req.rows}-${req.start}-${req.end}.pdf`
  const m = disposition === null ? null : /filename="?([^";]+)"?/i.exec(disposition)
  const name = m?.[1]?.trim()
  return name !== undefined && /^[\w.-]+\.pdf$/i.test(name) && !name.includes('..') ? name : fallback
}

/**
 * The report as a PDF: the TABLE's request (every row the server lists), plus a `title` for a preset. Goes through
 * the authenticated client, so it is a fetch of bytes and never a link.
 */
export async function fetchReportPdf(req: ReportQueryRequest, title: string | null): Promise<{ blob: Blob; filename: string }> {
  const { data, error, response } = await api.GET('/api/v1/reports/query/pdf', {
    params: { query: { ...reportQueryParams(req, TABLE_LIMIT), ...(title === null ? {} : { title }) } },
    parseAs: 'blob',
  })
  if (error !== undefined) throw new PdfExportError(response.status === 422 && fieldOf(error) === 'granularity' ? 'too_many_periods' : 'failed')
  if (!(data instanceof Blob)) throw new PdfExportError('failed')
  return { blob: data, filename: pdfFilename(response.headers.get('Content-Disposition'), req) }
}
